/**
 * steps/StrategyIntroduction.tsx
 * Strategy introduction step for the onboarding wizard
 *
 * Educational screen that introduces users to the strategy system
 * before they begin building their first strategy. Explains the
 * hierarchy of Strategy → Playbooks → Entry Models/Criteria.
 */

// 1. External dependencies
import React, { useState, useEffect, useRef, useCallback } from 'react';

// 2. Internal absolute imports
import { Card, CardBody, CardHeader, CardFooter, Button } from '@enfomentis/ui';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';

/**
 * Custom hook for intersection observer
 */
const useIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: Partial<{
    threshold: number;
    root: Element | null;
    rootMargin: string;
  }>
) => {
  const [elements, setElements] = useState<Element[]>([]);

  useEffect(() => {
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(callback, {
      threshold: 0.3,
      ...options
    });

    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [elements, callback, options]);

  const addElement = useCallback((element: Element | null) => {
    if (element && !elements.includes(element)) {
      setElements(prev => [...prev, element]);
    }
  }, [elements]);

  return addElement;
};

/**
 * Strategy Introduction component props
 */
interface StrategyIntroductionProps {
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}

export const StrategyIntroduction: React.FC<StrategyIntroductionProps> = ({ goToNextStep, goToPreviousStep }) => {
  const { markCompleted } = useOnboardingData();

  // State for scroll-based engagement
  const [revealedSections, setRevealedSections] = useState<Set<string>>(new Set()); // Start empty - reveal as user scrolls
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Intersection observer callback
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const sectionId = entry.target.getAttribute('data-section-id');
        if (sectionId) {
          setRevealedSections(prev => new Set([...prev, sectionId]));
        }
      }
    });
  }, []);

  // Set up intersection observer
  const addToObserver = useIntersectionObserver(handleIntersection);

  // Show intro section immediately on mount
  useEffect(() => {
    setRevealedSections(prev => new Set([...prev, 'intro']));
  }, []);

  // Handler for "Learn More" - scrolls to second card
  const handleLearnMore = useCallback(() => {
    const secondCard = scrollContainerRef.current?.querySelector('[data-section-id="strategy"]');
    secondCard?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Handler for continue button - marks complete and goes to next step
  const handleContinue = useCallback(() => {
    markCompleted('strategy-intro');
    goToNextStep();
  }, [markCompleted, goToNextStep]);

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-dvh w-full flex flex-col border-0"
    >
      <CardBody className="flex-1 p-0">
        <div
          ref={scrollContainerRef}
          className="h-full overflow-y-auto px-6 scroll-smooth [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-0 scrollbar-thin scrollbar-thumb-default-300 scrollbar-track-transparent"
          style={{
            scrollSnapType: 'y proximity',
            scrollBehavior: 'smooth'
          }}
        >
          
          {/* Card 1: Introduction to Strategies */}
          <div
            data-section-id="intro"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 ${
              revealedSections.has('intro')
                ? 'opacity-100 translate-y-0'
                : 'opacity-100 translate-y-0'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Introduction to Strategies
              </CardHeader>
              <CardBody className="pt-10 pb-15">
                <div className="space-y-4 text-base leading-relaxed text-text-secondary pt-2">
                  <div>
                    In the next stage of onboarding, you will build out your trading strategy.
                  </div>
                  <div>
                    A <span className="text-primary font-semibold">structured strategy</span> helps you maintain discipline, trade consistently, and learn from your data. Instead of making emotional decisions in the moment, you'll follow a systematic approach that you can refine over time.
                  </div>
                </div>
              </CardBody>
              <CardFooter className="border-t-1 pt-6 pb-6 flex flex-col gap-4">
                <Button
                  color="primary"
                  size="lg"
                  className="w-full"
                  onPress={handleLearnMore}
                >
                  LEARN MORE
                </Button>
                <div className="flex justify-between items-center w-full">
                  <Button
                    variant="light"
                    onPress={goToPreviousStep}
                    startContent={<span>←</span>}
                    className="flex-1 justify-start"
                  >
                    Back
                  </Button>
                  <Button
                    variant="light"
                    onPress={handleContinue}
                    endContent={<span>→</span>}
                    className="flex-1 justify-end"
                  >
                    Skip Education
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 2: What is a Trading Strategy? */}
          <div
            data-section-id="strategy"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-100 ${
              revealedSections.has('strategy')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                What is a Trading Strategy?
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  A strategy is your trading philosophy - the main container for your entire trading plan.
                </p>
                <p className="text-base leading-relaxed text-text-secondary mt-4">
                  Your strategy represents your <span className="text-primary font-semibold">overall approach</span> to the markets and what you're trying to accomplish.
                </p>
                <div className="mt-6 space-y-3">
                  <p className="text-sm font-semibold text-text-primary">Examples:</p>
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardBody className="space-y-3">
                      <div className="text-sm leading-relaxed">
                        <span className="font-semibold text-text-primary">NQ H01 Liquidity Scalping</span>
                        <p className="text-text-secondary mt-1">Capitalizing on moves between hourly liquidity pools</p>
                      </div>
                      <div className="text-sm leading-relaxed">
                        <span className="font-semibold text-text-primary">Forex/Metal Daily Target Trading</span>
                        <p className="text-text-secondary mt-1">Trading larger daily objectives using a five minute entry model once hourly structure aligns with daily objectives</p>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                Each strategy you create will contain specific playbooks that define how you execute this philosophy.
              </CardFooter>
            </Card>
          </div>

          {/* Card 3: What is a Playbook? */}
          <div
            data-section-id="playbook"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-200 ${
              revealedSections.has('playbook')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                What is a Playbook?
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  A playbook is a <span className="text-primary font-semibold">specific market scenario</span> or setup within your strategy.
                </p>
                <p className="text-base leading-relaxed text-text-secondary mt-4">
                  While your strategy defines your overall philosophy, playbooks are the actual situations you look for to execute trades.
                </p>
                <div className="mt-6 space-y-3">
                  <p className="text-sm font-semibold text-text-primary">Examples:</p>
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardHeader className="text-sm font-semibold">
                      Within "NQ H01 Liquidity Scalping"
                    </CardHeader>
                    <CardBody className="pt-0">
                      <ul className="space-y-2">
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Session Liquidity Sweep in line with Hourly Impulse
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Failed Session Sweep with More Targets Past Sweep Level
                        </li>
                      </ul>
                    </CardBody>
                  </Card>
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardHeader className="text-sm font-semibold">
                      Within "Forex/Metal Daily Target Trading"
                    </CardHeader>
                    <CardBody className="pt-0">
                      <ul className="space-y-2">
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Pullback to Daily Level with H01 Confirmation
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Breakout Continuation After Range Compression
                        </li>
                      </ul>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                Each playbook will have its own criteria and entry modelsthat define exactly when and how you trade it.
              </CardFooter>
            </Card>
          </div>

          {/* Card 4: What are Criteria? */}
          <div
            data-section-id="criteria"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-300 ${
              revealedSections.has('criteria')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                What are Criteria?
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Criteria are <span className="text-primary font-semibold">conditions that MUST be met</span> for your playbook to be valid.
                </p>
                <p className="text-base leading-relaxed text-text-secondary mt-4">
                  These act as hard filters - all criteria must be present before you consider taking a trade.
                </p>
                <div className="mt-6 space-y-3">
                  <p className="text-sm font-semibold text-text-primary">Examples:</p>
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardBody>
                      <ul className="space-y-2">
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Price above VWAP
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Volume greater than 20-period average
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Hourly structure aligned with daily bias
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Price within 10 ticks of key liquidity level
                        </li>
                      </ul>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                <div>If any criterion is missing, you don't take the trade. This <span className="text-primary font-semibold">keeps you disciplined</span> and prevents impulsive decisions.</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 5: What are Confluences? */}
          <div
            data-section-id="confluences"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-400 ${
              revealedSections.has('confluences')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                What are Confluences?
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  Confluences are factors that <span className="text-primary font-semibold">affect your confidence</span> in a trade.
                </p>
                <p className="text-base leading-relaxed text-text-secondary mt-4">
                  Unlike criteria which must all be met, confluences are situational — they help you assess whether conditions favor the trade.
                </p>
                <div className="mt-6 space-y-3">
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardHeader className="text-sm font-semibold text-success">
                      Positive Confluences
                    </CardHeader>
                    <CardBody className="pt-0">
                      <ul className="space-y-2">
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-success">•</span> Volume confirms the impulse move
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-success">•</span> Multiple timeframes aligned
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-success">•</span> Clean, impulsive price action
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-success">•</span> Key level holding as expected
                        </li>
                      </ul>
                    </CardBody>
                  </Card>
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardHeader className="text-sm font-semibold text-warning">
                      Negative Confluences
                    </CardHeader>
                    <CardBody className="pt-0">
                      <ul className="space-y-2">
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-warning">•</span> Major news event within 30 minutes
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-warning">•</span> Choppy, overlapping price action
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-warning">•</span> Low volume during typical high-activity period
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-warning">•</span> Multiple failed attempts at same level
                        </li>
                      </ul>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                More positive confluences? Size up or use an aggressive entry. More negative? Reduce size, tighten stops, or pass.
              </CardFooter>
            </Card>
          </div>

          {/* Card 6: What is an Entry Model? */}
          <div
            data-section-id="entry-model"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-500 ${
              revealedSections.has('entry-model')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                What is an Entry Model?
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  An entry model defines exactly WHEN and HOW you enter a trade.
                </p>
                <p className="text-base leading-relaxed text-text-secondary mt-4">
                  Once your criteria are met and you've assessed negative confluences, your entry model tells you the <span className="text-primary font-semibold">precise trigger and execution method</span>.
                </p>
                <div className="mt-6 space-y-3">
                  <p className="text-sm font-semibold text-text-primary">Examples:</p>
                  <Card radius="sm" shadow="sm" className="bg-default-100">
                    <CardBody>
                      <ul className="space-y-2">
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Market order on break of 5-minute candle high
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Limit order at 50% retracement of impulse move
                        </li>
                        <li className="text-sm leading-relaxed text-text-secondary">
                          <span className="text-primary">•</span> Stop order at previous session high + 2 ticks
                        </li>
                      </ul>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                The same playbook might have multiple entry models depending on market conditions — aggressive entries when positive confluences dominate, conservative entries when negative confluences are present.
              </CardFooter>
            </Card>
          </div>

          {/* Card 7: How It All Works Together */}
          <div
            data-section-id="together"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-600 ${
              revealedSections.has('together')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                How It All Works Together
              </CardHeader>
              <CardBody className="pt-0">
                <div className="space-y-4 text-text-secondary">
                  
                  <p className="text-base leading-relaxed text-text-secondary pt-2">
                    Here's how the hierarchy flows when you're trading:
                  </p>
                  <Card radius="sm" shadow="sm" className="bg-default-100 font-mono">
                    <CardHeader className="text-lg">
                      Strategy - the basic philosophy
                    </CardHeader>
                    <CardBody className="font-mono space-y-1">
                      <div className="ml-4 text-text-primary"><span className="font-semibold">Playbook: </span> The setup within the strategy</div>
                      <div className="ml-8 text-text-secondary">↳ <span className="font-semibold text-text-primary">Criteria: </span> Conditions that must be present</div>
                      <div className="ml-8 text-text-secondary">↳ <span className="font-semibold text-text-primary">Confluences: </span> Factors affecting confidence</div>
                      <div className="ml-8 text-text-secondary">↳ <span className="font-semibold text-text-primary">Entry Model: </span> Entry trigger</div>
                    </CardBody>
                  </Card>
                  <Card radius="sm" shadow="sm" className='font-serif bg-default-100'>
                    <CardHeader>
                      <div className="text-text-primary"><span className="font-semibold">Example Strategy:</span> Hourly Scalping</div>
                    </CardHeader>
                    <CardBody>
                      <div className="space-y-1 ml-2">
                        <div className="text-text-primary"><span className="font-semibold">Playbook: </span>Session Liquidity Sweep with Hourly Impulse</div>
                        <div className="ml-4 text-text-primary">↳ <span className="font-semibold text-text-primary">Criteria: </span> Price swept session level, hourly impulse in line with trade idea, volume elevated</div>
                        <div className="ml-4 text-text-primary">↳ <span className="font-semibold text-text-primary">Confluences: </span> Volume confirms impulse (+), more liquidity targets beyond sweep (-)</div>
                        <div className="ml-4 text-text-primary">↳ <span className="font-semibold text-text-primary">Entry Model: </span> 1-minute market structure shift EQ limit entry</div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </CardBody>
              <CardFooter className="text-text-secondary text-base leading-relaxed border-t-1">
                  <div>This systematic approach ensures <span className="text-primary font-semibold">every trade follows your plan</span>, making it easier to stay disciplined and learn from your results.</div>
              </CardFooter>
            </Card>
          </div>

          {/* Card 8: Ready to Build Your Strategy */}
          <div
            data-section-id="ready"
            ref={addToObserver}
            className={`min-h-[calc(100vh-120px)] flex items-start justify-center pt-8 transition-all duration-700 delay-700 ${
              revealedSections.has('ready')
                ? 'opacity-100 translate-y-0'
                : 'opacity-30 translate-y-8'
            }`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <Card className="w-full max-w-2xl">
              <CardHeader className="pb-3 text-2xl font-bold border-b-1">
                Ready to Build Your Strategy?
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-base leading-relaxed text-text-secondary pt-2">
                  You'll start by building a custom strategy from scratch.
                </p>
                <p className="text-base leading-relaxed text-text-secondary mt-4">
                  This hands-on approach helps you understand each component and create a strategy that matches your trading style.
                </p>
                <p className="text-text-secondary text-base leading-relaxed mt-4">
                  In the future, we'll add common strategy templates to help you get started even faster.
                </p>
              </CardBody>
              <CardFooter className="border-t-1 pt-6 pb-6">
                <Button
                  color="primary"
                  size="lg"
                  className="w-full"
                  onPress={handleContinue}
                >
                  Continue to Strategy Builder
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};